import axios from 'axios';
import { ISession, ApiContext, IApiContext, IImpersonate } from 'api/api-context';
import { ActionName, actions, GetActionMap } from 'api/actions/actions';
import { AuthLoggedUserInfoResponse } from 'api/actions/auth-logged-user-info/auth-logged-user-info-response';
import { AuthLoginResponse } from 'api/actions/auth-login/auth-login-response';
import { hasPermissionInMap } from 'api/has-permission-in-map';
import useLocalStorage from 'hooks/use-local-storage';
import { REST_API_TIMEOUT, REST_API_URL } from 'env';
import isSessionAuthError from 'errors/is-session-auth-error';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LOGIN_PAGE_PATH, LOGOUT_PAGE_PATH, PASSWORD_CHANGE_REQUIRED_PAGE_PATH } from 'routes/paths';
import { notifications } from '@mantine/notifications';
import { PASSWORD_CHANGE_REQUIRED_ERROR_CODE, SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

/**
 * Creates an empty session.
 */
const createEmptySession = () => ({
  jwt: '',
  firstName: '',
  lastName: '',
  fullName: '',
  email: '',
  phoneNumber: '',
  userId: -1,
  role: '',
  roleId: -1,
  organizationId: null,
  organizationHierarchyId: null,
  legacyDepartmentUi: false,
  permissions: null,
  organizationName: '',
  priceOffersEnabled: false,
  hrEnabled: false,
  revisionsEnabled: false,
  formsEnabled: false,
  revisionTemplatesEnabled: false,
  twoFactorEnabled: false,
  twoFactorSetupPending: false,
  twoFactorRequired: false,
  twoFactorRecoveryCodesRemaining: 0,
  passwordChangeRequired: false,
  twoFactorSetupCompletedAt: null,
});

/**
 * Normalizes optional 2FA fields so older API responses cannot persist undefined values.
 */
function getTwoFactorSessionState(response: Partial<AuthLoggedUserInfoResponse | AuthLoginResponse>) {
  return {
    twoFactorEnabled: response.twoFactorEnabled ?? false,
    twoFactorSetupPending: response.twoFactorSetupPending ?? false,
    twoFactorRequired: response.twoFactorRequired ?? false,
    twoFactorRecoveryCodesRemaining: response.twoFactorRecoveryCodesRemaining ?? 0,
    twoFactorSetupCompletedAt: response.twoFactorSetupCompletedAt ?? null,
  };
}

/**
 * Normalizes organization module fields for rolling compatibility with an older API response.
 */
export function getOrganizationModuleSessionState(response: Partial<AuthLoggedUserInfoResponse | AuthLoginResponse>) {
  const belongsToOrganization = response.organizationId !== undefined && response.organizationId !== null;

  return {
    priceOffersEnabled: response.priceOffersEnabled ?? belongsToOrganization,
    hrEnabled: response.hrEnabled ?? belongsToOrganization,
    revisionsEnabled: response.revisionsEnabled ?? belongsToOrganization,
    formsEnabled: response.formsEnabled ?? belongsToOrganization,
    revisionTemplatesEnabled: response.revisionTemplatesEnabled ?? false,
  };
}

/** Converts an authenticated response into persisted impersonation state without copying the JWT. */
function createImpersonateSession(response: AuthLoggedUserInfoResponse): IImpersonate {
  const { token: _token, ...identity } = response;

  return {
    ...identity,
    ...getTwoFactorSessionState(response),
    ...getOrganizationModuleSessionState(response),
    legacyDepartmentUi: response.legacyDepartmentUi ?? false,
    passwordChangeRequired: response.passwordChangeRequired ?? false,
  };
}

/**
 * Builds the forced password-change URL and optionally preserves the original destination.
 */
export function buildPasswordChangeRequiredUrl(returnUrl?: string) {
  if (!returnUrl || returnUrl === PASSWORD_CHANGE_REQUIRED_PAGE_PATH.original) {
    return PASSWORD_CHANGE_REQUIRED_PAGE_PATH.original;
  }

  return `${PASSWORD_CHANGE_REQUIRED_PAGE_PATH.original}?returnUrl=${encodeURIComponent(returnUrl)}`;
}

/**
 * The session provider is used to provide the session context.
 */
export default function ApiProvider({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useState(false);
  const [shouldResumeSession, setShouldResumeSession] = useState(false);
  const [{ jwt, ...session }, setSession, updateSession] = useLocalStorage<ISession>(
    'objekto.session',
    createEmptySession()
  );

  const [{ impersonate }, setImpersonateImpl] = useLocalStorage<{ impersonate?: IImpersonate }>(
    'objekto.impersonate',
    {}
  );

  const setImpersonate = useCallback(
    (impersonate: IImpersonate | undefined) => setImpersonateImpl({ impersonate }),
    [setImpersonateImpl]
  );

  const firstName = impersonate?.firstName ?? session.firstName;
  const lastName = impersonate?.lastName ?? session.lastName;
  const fullName = impersonate?.fullName ?? session.fullName;
  const email = impersonate?.email ?? session.email;
  const phoneNumber = impersonate?.phoneNumber ?? session.phoneNumber;
  const userId = impersonate?.userId ?? session.userId;
  const role = impersonate?.role ?? session.role;
  const roleId = impersonate?.roleId ?? session.roleId;
  const organizationId = impersonate ? impersonate.organizationId ?? null : session.organizationId ?? null;
  const organizationHierarchyId = impersonate
    ? impersonate.organizationHierarchyId ?? null
    : session.organizationHierarchyId ?? null;
  const legacyDepartmentUi = impersonate
    ? impersonate.legacyDepartmentUi === true
    : session.legacyDepartmentUi === true;
  const permissions = impersonate?.permissions ?? session.permissions;
  const organizationName = impersonate?.organizationName ?? session.organizationName;
  const impersonatedBelongsToOrganization = Boolean(
    impersonate?.organizationName && impersonate.organizationName !== '-'
  );
  const priceOffersEnabled = impersonate ? impersonate.priceOffersEnabled ?? false : session.priceOffersEnabled;
  const hrEnabled = impersonate ? impersonate.hrEnabled ?? false : session.hrEnabled;
  const revisionsEnabled = impersonate
    ? impersonate.revisionsEnabled ?? impersonatedBelongsToOrganization
    : session.revisionsEnabled ?? false;
  const formsEnabled = impersonate
    ? impersonate.formsEnabled ?? impersonatedBelongsToOrganization
    : session.formsEnabled ?? false;
  const revisionTemplatesEnabled = impersonate
    ? impersonate.revisionTemplatesEnabled ?? false
    : session.revisionTemplatesEnabled ?? false;
  const twoFactorEnabled = impersonate?.twoFactorEnabled ?? session.twoFactorEnabled;
  const twoFactorSetupPending = impersonate?.twoFactorSetupPending ?? session.twoFactorSetupPending;
  const twoFactorRequired = impersonate?.twoFactorRequired ?? session.twoFactorRequired;
  const twoFactorRecoveryCodesRemaining =
    impersonate?.twoFactorRecoveryCodesRemaining ?? session.twoFactorRecoveryCodesRemaining;
  const twoFactorSetupCompletedAt = impersonate?.twoFactorSetupCompletedAt ?? session.twoFactorSetupCompletedAt;
  const passwordChangeRequired = session.passwordChangeRequired;

  const storeSessionFromResponse = useCallback(
    <T extends AuthLoggedUserInfoResponse | AuthLoginResponse>(response: T): T => {
      if (!response.token) {
        return response;
      }

      const twoFactorSessionState = getTwoFactorSessionState(response);
      const organizationModuleSessionState = getOrganizationModuleSessionState(response);

      setSession({
        jwt: response.token,
        firstName: response.firstName,
        lastName: response.lastName,
        fullName: response.fullName,
        email: response.email,
        phoneNumber: response.phoneNumber ?? null,
        userId: response.userId,
        role: response.role,
        roleId: response.roleId,
        organizationId: response.organizationId ?? null,
        organizationHierarchyId: response.organizationHierarchyId ?? null,
        legacyDepartmentUi: response.legacyDepartmentUi ?? false,
        permissions: response.permissions,
        organizationName: response.organizationName,
        ...organizationModuleSessionState,
        ...twoFactorSessionState,
        passwordChangeRequired: response.passwordChangeRequired ?? false,
      });

      return response;
    },
    [setSession]
  );

  /**
   * Destroys the session.
   */
  const destroySession = useCallback(() => {
    if (jwt) {
      setSession(createEmptySession());
      setImpersonate(undefined);
      const currentPath = window.location.pathname + window.location.search;
      const returnUrl = currentPath !== LOGIN_PAGE_PATH.original ? `&returnUrl=${encodeURIComponent(currentPath)}` : '';
      window.open(`${LOGIN_PAGE_PATH.original}?logout=true${returnUrl}`, '_self');
    }
  }, [jwt, setSession, setImpersonate]);

  /**
   * Creates a contextualized action.
   */
  const getAction: GetActionMap = useMemo(() => {
    const connector = axios.create({
      timeout: REST_API_TIMEOUT,
      baseURL: REST_API_URL,
    });

    /**
     * Adds JWT to the request.
     */
    connector.interceptors.request.use((cfg) => {
      const skipImpersonation = cfg.headers['X-Impersonate'] === false;
      if (skipImpersonation) {
        delete cfg.headers['X-Impersonate'];
      }

      if (jwt) {
        cfg.headers.Authorization = `Bearer ${jwt}`;
        cfg.headers['X-User'] = session.userId;
      }

      if (impersonate && ready && !skipImpersonation) {
        cfg.headers['X-Impersonate'] = impersonate.userId;
      }

      return cfg;
    });

    connector.interceptors.response.use(
      (response) => response.data.response,
      (error) => {
        if (error?.response?.data?.error?.code === PASSWORD_CHANGE_REQUIRED_ERROR_CODE && jwt) {
          updateSession({ passwordChangeRequired: true });

          const currentPathname = window.location.pathname;
          const currentPath = currentPathname + window.location.search;
          const isPasswordRecoveryRoute =
            currentPathname === PASSWORD_CHANGE_REQUIRED_PAGE_PATH.original ||
            currentPathname === LOGOUT_PAGE_PATH.original;

          if (!isPasswordRecoveryRoute) {
            window.open(buildPasswordChangeRequiredUrl(currentPath), '_self');
          }
        }

        if (isSessionAuthError(error)) {
          destroySession();
        }

        return Promise.reject(error);
      }
    );

    const contextualizedActions = new Map(
      Array.from(actions.entries()).map(([name, createAction]) => [name, createAction(connector)])
    );

    return (action: ActionName) => contextualizedActions.get(action) as any;
  }, [jwt, impersonate, ready]);

  /**
   * Logs the user in and stores the session.
   */
  const login: IApiContext['login'] = useCallback(
    ({ payload: { email, password, rememberMe } }) => {
      const loginAction = getAction('AuthLogin');

      return loginAction({ payload: { email, password, rememberMe } }).then(storeSessionFromResponse);
    },
    [getAction, storeSessionFromResponse]
  );

  /**
   * Completes login after a two factor challenge.
   */
  const completeTwoFactorLogin: IApiContext['completeTwoFactorLogin'] = useCallback(
    ({ payload }) => {
      const verifyTwoFactorLoginAction = getAction('AuthVerifyTwoFactorLogin');

      return verifyTwoFactorLoginAction({ payload }).then(storeSessionFromResponse);
    },
    [getAction, storeSessionFromResponse]
  );

  /**
   * Initialize the session.
   */
  const initSession = useCallback(async () => {
    const userGetMeAction = getAction('AuthLoggedUserInfo');

    const response = await userGetMeAction({ headers: { 'X-Impersonate': false as never } });
    storeSessionFromResponse(response);

    if (impersonate?.userId) {
      const refreshedImpersonate = await userGetMeAction({ headers: { 'X-Impersonate': impersonate.userId } });
      setImpersonate(createImpersonateSession(refreshedImpersonate));
    }

    return response;
  }, [getAction, impersonate?.userId, setImpersonate, storeSessionFromResponse]);

  const refreshSession: IApiContext['refreshSession'] = useCallback(async () => {
    const userGetMeAction = getAction('AuthLoggedUserInfo');

    try {
      const response = await userGetMeAction({ headers: { 'X-Impersonate': false as never } });
      storeSessionFromResponse(response);

      if (impersonate?.userId) {
        const refreshedImpersonate = await userGetMeAction({ headers: { 'X-Impersonate': impersonate.userId } });
        setImpersonate(createImpersonateSession(refreshedImpersonate));
      }

      return response;
    } catch (err) {
      if (isSessionAuthError(err)) {
        destroySession();
      }

      throw err;
    }
  }, [destroySession, getAction, impersonate?.userId, setImpersonate, storeSessionFromResponse]);

  const hasPermissionAnywhere = useCallback(
    (permission: string) => {
      const map = (permissions?.anywhere ?? {}) as Record<string, boolean>;
      return hasPermissionInMap(map, permission);
    },
    [permissions]
  );

  const hasPermissionGlobally = useCallback(
    (permission: string) => {
      const map = (permissions?.global ?? {}) as Record<string, boolean>;
      return hasPermissionInMap(map, permission);
    },
    [permissions]
  );

  // Init session.
  useEffect(() => {
    if (jwt) {
      initSession()
        .catch((error) => {
          updateSession({ organizationHierarchyId: null });
          updateSession({ legacyDepartmentUi: false });
          if (impersonate) setImpersonate({ ...impersonate, organizationHierarchyId: null, legacyDepartmentUi: false });
          if (isSessionAuthError(error)) {
            setShouldResumeSession(true);
          }
        })
        .finally(() => setReady(true));
    } else {
      setReady(true);
    }
  }, []); // Run only once.

  // Show notification when impersonating.
  useEffect(() => {
    if (impersonate) {
      notifications.show({
        color: SUCCESS_NOTIFICATION_COLOR,
        title: `Vystupujete ako ${impersonate.fullName}`,
        message: 'Pre návrat do svojho účtu kliknite na tlačidlo krížika pri mene užívateľa v ľavom dolnom rohu.',
      });
    }
  }, [impersonate]);

  return (
    <ApiContext.Provider
      value={{
        ready,
        shouldResumeSession,
        jwt,
        firstName,
        lastName,
        fullName,
        email,
        phoneNumber,
        userId,
        role,
        roleId,
        organizationId,
        organizationHierarchyId,
        legacyDepartmentUi,
        permissions,
        organizationName,
        priceOffersEnabled,
        hrEnabled,
        revisionsEnabled,
        formsEnabled,
        revisionTemplatesEnabled,
        twoFactorEnabled,
        twoFactorSetupPending,
        twoFactorRequired,
        twoFactorRecoveryCodesRemaining,
        passwordChangeRequired,
        twoFactorSetupCompletedAt,
        impersonate,
        originalSession: session,
        hasPermissionAnywhere,
        hasPermissionGlobally,
        logout: destroySession,
        getAction,
        login,
        completeTwoFactorLogin,
        refreshSession,
        setImpersonate,
      }}
    >
      {ready ? children : <></>}
    </ApiContext.Provider>
  );
}
